import { Button, FormControlLabel, MenuItem, Stack, Switch, TextField } from '@mui/material';
import { type FC, useEffect, useState } from 'react';

import { type StartVisionReplayBody } from '../../api/index.js';
import { type CameraVisionSettings } from '../../models/index.js';
import VisionSettingsFields from '../Camera/VisionSettingsFields.js';

const SAMPLE_RATES = [1, 2, 3, 5];

export type ReplayOptions = Required<StartVisionReplayBody>;

interface Props {
  value: ReplayOptions;
  savedSettings: CameraVisionSettings;
  running: boolean;
  saving: boolean;
  onRun: (options: ReplayOptions) => void;
  onSave: (settings: CameraVisionSettings) => void;
}

function sameSettings(left: CameraVisionSettings, right: CameraVisionSettings): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

const VisionReplayOptions: FC<Props> = ({ value, savedSettings, running, saving, onRun, onSave }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const replayedSettingsUnsaved = !sameSettings(value.settings, savedSettings);

  return (
    <Stack spacing={1.5}>
      <Stack direction={'row'} spacing={2} alignItems={'center'}>
        <TextField
          select
          size={'small'}
          label={'Frames per second'}
          value={draft.sampleFps}
          onChange={(event) => setDraft({ ...draft, sampleFps: Number(event.target.value) })}
          sx={{ minWidth: 140 }}
        >
          {SAMPLE_RATES.map((rate) => <MenuItem key={rate} value={rate}>{rate}</MenuItem>)}
        </TextField>
        <FormControlLabel
          control={
            <Switch checked={draft.tracking} onChange={(event) => setDraft({ ...draft, tracking: event.target.checked })} />
          }
          label={'Tracking'}
        />
      </Stack>
      <VisionSettingsFields value={draft.settings} onChange={(settings) => setDraft({ ...draft, settings })} />
      <Stack direction={'row'} spacing={1}>
        <Button variant={'outlined'} size={'small'} disabled={running} onClick={() => onRun(draft)}>
          Run again
        </Button>
        <Button
          size={'small'}
          disabled={running || saving || !replayedSettingsUnsaved}
          onClick={() => onSave(value.settings)}
        >
          Save as camera settings
        </Button>
      </Stack>
    </Stack>
  );
};

export default VisionReplayOptions;
